import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataChange } from 'devextreme/common/grids';
import Tabs, { Item } from 'devextreme-react/tabs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Toast } from 'devextreme-react/toast';
import { formatMessage } from 'devextreme/localization';

import { Button, TextBox } from 'devextreme-react';

import MessageForm, { MessageFormHandle, ErrorMessage } from '../../components/message-form/MessageForm';
import PathApi from '../../api/PathApi';
import PathVersionStatus from '../../components/path-version-status/PathVersionStatus';
import ChangeInProgressForm from '../../components/change-in-progress-form/ChangeInProgressForm';

import SourceSelection from '../../components/source-selection/SourceSelection';
import FiltersSelection from '../../components/filters-selection/FiltersSelection';
import useFilteringCapabilities from '../../components/filters-selection/useFilteringCapabilities';
import { collectDeclaredParameters, collectSelectedFieldReferenceIds } from '../../components/filter-selection/FilterBuilderInline';

import SynchroMappingFields, { SynchroMappingFieldsHandle } from '../../components/synchro-mapping-fields/SynchroMappingFields';
import SynchroSetup from '../../components/synchro-setup/SynchroSetup';
import SynchroResume, { SynchroFieldResume } from '../../components/synchro-resume/SynchroResume';
import SynchroExecution from '../../components/synchro-execution/SynchroExecution';

import PathSynchroApi from '../../api/PathSynchroApi';

import GetPathSynchroDetailResult from '../../classes/api/result/pathsynchromodule/GetPathSynchroDetailResult';
import PathSynchroMappingDto from '../../classes/dtos/pathsynchromodule/PathSynchroMappingDto';

import DataTableReadDto from '../../classes/dtos/pathcommonmodule/DataTableReadDto';
import DataSourceDataTableReadDto from '../../classes/dtos/pathcommonmodule/DataSourceDataTableReadDto';
import PathDataTableReadDto from '../../classes/dtos/pathcommonmodule/PathDataTableReadDto';

import SynchroFieldMappingDto from '../../classes/dtos/pathsynchromodule/SynchroFieldMappingDto';

import PathCheckResultDto from '../../classes/dtos/pathmodule/PathCheckResultDto';
import { checkPathBeforeSaveAndPublish, getPathPublishChecklist, PathBeforeSaveValidationInput, ValidationRow } from '../../logics/pathmodule/PathBeforeSaveValidation';
import { performPathSave } from '../../logics/pathmodule/performPathSave';
import { useAuth } from '../../contexts/auth';
import PathSynchroSetupDto from '../../classes/dtos/pathsynchromodule/PathSynchroSetupDto';
import ScheduleDto from '../../classes/dtos/schedulermodule/ScheduleDto';

import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';
import TransformationNotConfigurableDto from '../../classes/dtos/transformationmodule/TransformationNotConfigurableDto';
import FieldReferenceIdAndKeyDto from '../../classes/dtos/FieldReferenceIdAndKeyDto';
import PathSynchroMappingCapabilitiesDto from '../../classes/dtos/pathsynchromodule/PathSynchroMappingCapabilitiesDto';

import DataTypeDto from '../../classes/dtos/datastructuremodule/DataTypeDto';

// Compare l'identité "table source" (TableReferenceId ou PathId selon le type) plutôt que l'égalité
// d'objet : FiltersSelection reconstruit un nouveau DataTableReadDto à chaque édition de filtre/paramètre
// tout en gardant la même table référencée.
function isSameDataTable(a: DataTableReadDto | null, b: DataTableReadDto | null): boolean {
    if (a === null || b === null) {
        return a === b;
    }
    if (a instanceof DataSourceDataTableReadDto && b instanceof DataSourceDataTableReadDto) {
        return a.TableReferenceId === b.TableReferenceId;
    }
    if (a instanceof PathDataTableReadDto && b instanceof PathDataTableReadDto) {
        return a.PathId === b.PathId;
    }
    return false;
}

export default function PathSynchro() {

    const { id } = useParams();


    const { sessionDetail } = useAuth();
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const pathId = Number.parseInt(id ?? "0");

    // Feature flag BD (Feature.FilterVisibility) : première condition d'affichage de l'onglet "Filters".
    const filterTabsEnabled = sessionDetail?.WithFilterVisibility ?? false;

    const messageFormHandleRef = useRef<MessageFormHandle>(null);

    const synchroMappingFieldsRef = useRef<SynchroMappingFieldsHandle>(null);

    const [pathSynchroDetail, setPathSynchroDetail] = useState<GetPathSynchroDetailResult | null>(null);

    // "Advanced mode" checkbox (FILTER column header). Page-level state, persisted on the mapping version.
    const [advancedFilterMode, setAdvancedFilterMode] = useState<boolean>(false);
    // Advanced mode is a one-way switch: once it has been saved it can't be turned back off.
    const [advancedFilterModeLocked, setAdvancedFilterModeLocked] = useState<boolean>(false);

    // L'onglet "Filters" (mode complexe) n'est disponible que si la feature BD est active ET que le mode avancé
    // est activé sur ce path — fusion du feature flag Feature.FilterVisibility et de l'ancien `!simpleFilterMode`.
    const filtersTabAvailable = filterTabsEnabled && advancedFilterMode;

    // Colonne FILTER des grilles : visible seulement si la table source gère le filtrage. Même calcul
    // (`withFilter`) que l'onglet "Filters" dans FiltersSelection, via le hook partagé.
    const filterSourceTableReferenceId = pathSynchroDetail?.Source instanceof DataSourceDataTableReadDto ? (pathSynchroDetail.Source.TableReferenceId ?? 0) : 0;
    const { withFilter: sourceHandlesFilters } = useFilteringCapabilities(filterSourceTableReferenceId);

    const [sourceFields, setSourceFields] = useState<FieldReferenceIdAndKeyDto[]>([]);
    const [targetFields, setTargetFields] = useState<FieldReferenceIdAndKeyDto[]>([]);
    const [capabilities, setCapabilities] = useState<PathSynchroMappingCapabilitiesDto[]>([]);
    const [transformationsCapabilities, setTransformationsCapabilities] = useState<TransformationCapabilitiesDto[]>([]);

    const [tableReferenceSourceId, setTableReferenceSourceId] = useState<number | null>(null);
    const [pathSourceId, setPathSourceId] = useState<number | null>(null);
    const [tableReferenceTargetId, setTableReferenceTargetId] = useState<number | null>(null);


    const [mapping, setMapping] = useState<SynchroFieldMappingDto[]>([]);

    const SUMMARY_TAB = 5;
    const MAX_TAB = 6;
    const FILTERS_TAB = 3;

    const [currentTab, setCurrentTab] = useState<number>(() => {
        const tabFromUrl = Number(searchParams.get('tab'));
        const isValid = !Number.isNaN(tabFromUrl) && tabFromUrl >= 0 && tabFromUrl <= MAX_TAB && (filterTabsEnabled || tabFromUrl !== FILTERS_TAB);
        return isValid ? tabFromUrl : 0;
    });

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [changeInprogress, setChangeInprogress] = useState<boolean>(false);
    const [isPublished, setIsPublished] = useState<boolean>(false);

    const [editNameInprogress, setEditNameInprogress] = useState<string | null>(null);

    const [isLaunchable, setIsLaunchable] = useState<boolean>(false);

    const dataChangesRef = useRef<DataChange[]>([]);

    const pathSynchroMappingDtosRef = useRef<PathSynchroMappingDto[]>([]);

    // Clé (tables source/cible) du dernier dataLoad abouti : voir dataLoad.
    const loadedKeyRef = useRef<string>('');

    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: 'info',
        message: '',
        withRedirect: false
    } as {
        isVisible: boolean,
        type: 'info' | 'error' | 'success',
        message: string,
        withRedirect: boolean
    });

    useEffect(() => {
        PathSynchroApi.GetPathSynchroDetailAsync(pathId)
            .then(detail => {
                if (detail.IsFailed()) {
                    navigate("/paths");
                }
                else {
                    setPathSynchroDetail(detail.Result);
                    setAdvancedFilterMode(detail.Result.AdvancedFilterMode);
                    setAdvancedFilterModeLocked(detail.Result.AdvancedFilterMode);
                    setIsLaunchable((detail.Result).PublishedVersionId !== null)
                    setIsPublished((detail.Result).PublishedVersionId !== null && (detail.Result).PublishedVersionId === (detail.Result).LastVersionId);
                }
            });
    }, [pathId, navigate]);

    function GetPathSynchroMappingCapabilities(targetFieldId: number, sourceFieldId: number) {
        let currentCapabilities = capabilities.find((e) => e.TargetFieldId === targetFieldId) ?? null;
        if (currentCapabilities !== null) {
            return currentCapabilities.Capabilities.find((e) => e.SourceFieldId === sourceFieldId) ?? null;
        }
        return null;
    }

    function GetFieldName(targetFieldId: number) {
        return targetFields.find((e) => e.Id === targetFieldId)?.Name ?? "";
    }

    function getFields(targetFieldReferenceId: number, sourceFields: FieldReferenceIdAndKeyDto[], capabilities: PathSynchroMappingCapabilitiesDto[]): FieldReferenceIdAndKeyDto[] {
        let fields = new Array<FieldReferenceIdAndKeyDto>();
        sourceFields.forEach((sourceField) => {
            let currentCapabilities = capabilities.find((e) => e.TargetFieldId === targetFieldReferenceId) ?? null;
            if (currentCapabilities !== null) {
                let source = currentCapabilities.Capabilities.find((e) => e.SourceFieldId === sourceField.Id) ?? null;
                if (source !== null && source.TransformationLogicIds.length > 0) {
                    fields.push(sourceField);
                }
            }
        });
        return fields;
    }

    // Builds the grid's row model (one row per target field) from a persisted mapping list. Shared by
    // the initial load and by save's onSuccess, so a rebuild after publishing from another tab produces
    // rows identical to a fresh load.
    function buildMappingRows(
        mappings: PathSynchroMappingDto[],
        allFieldsTarget: FieldReferenceIdAndKeyDto[],
        allFieldsSource: FieldReferenceIdAndKeyDto[],
        mappingsCapabilities: PathSynchroMappingCapabilitiesDto[]
    ): SynchroFieldMappingDto[] {
        return allFieldsTarget.map((e) => {
            const readMapping = mappings.find(x => x.TargetFieldReferenceId === e.Id) ?? null;
            // A read-only target stays mappable (as a key only, enforced before publication); only an
            // unmanaged target or one without any compatible source is locked.
            const readOnly = (e.Type.Type === 0 || getFields(e.Id, allFieldsSource, mappingsCapabilities).length === 0);
            // The backend serializes SourceFieldReferenceId as a non-nullable int and uses 0 as its
            // "unmapped" sentinel; normalize it to null and treat such a row as fully unmapped.
            const sourceFieldReferenceId = (readMapping?.SourceFieldReferenceId ?? 0) || null;
            // Carry the per-row filter data through the rebuild: simple-mode conditions (Filters) and
            // complex-mode declared variables (DeclaredVariables). Without this a rebuild — initial load
            // or a save from a tab other than Mapping — drops them and the FILTER column comes back empty.
            const filters = readMapping?.Filters ?? [];
            const declaredVariables = readMapping?.DeclaredVariables ?? [];
            if (readMapping === null || sourceFieldReferenceId === null) {
                return new SynchroFieldMappingDto(false, null, null, null, e.Id, e.Name, e.Type, readOnly, filters, declaredVariables);
            }
            return new SynchroFieldMappingDto(readMapping.IsKey, sourceFieldReferenceId, readMapping.TransformationLogicId, readMapping.TransformationParameters, e.Id, e.Name, e.Type, readOnly, filters, declaredVariables);
        });
    }

    const dataLoad = useCallback(async () => {

        if (pathSynchroDetail === null) {
            return;
        }

        if (pathSynchroDetail.Source === null) {
            return;
        }

        if (pathSynchroDetail.Target === null) {
            return;
        }

        let tableReferenceSourceId: number | null = null;
        let pathSourceId: number | null = null;

        if (pathSynchroDetail.Source instanceof DataSourceDataTableReadDto) {
            if ((pathSynchroDetail.Source.DataSourceId ?? 0) > 0 && (pathSynchroDetail.Source.TableReferenceId ?? 0) > 0) {
                tableReferenceSourceId = pathSynchroDetail.Source.TableReferenceId;
            }
        }

        if (pathSynchroDetail.Source instanceof PathDataTableReadDto) {
            if ((pathSynchroDetail.Source.PathId ?? 0) > 0) {
                pathSourceId = pathSynchroDetail.Source.PathId;
            }
        }

        let tableReferenceTargetId: number | null = null;
        let pathTargetId: number | null = null;

        if (pathSynchroDetail.Target instanceof DataSourceDataTableReadDto) {
            if ((pathSynchroDetail.Target.DataSourceId ?? 0) > 0 && (pathSynchroDetail.Target.TableReferenceId ?? 0) > 0) {
                tableReferenceTargetId = pathSynchroDetail.Target.TableReferenceId;
            }
        }

        if (pathSynchroDetail.Target instanceof PathDataTableReadDto) {
            if ((pathSynchroDetail.Target.PathId ?? 0) > 0) {
                pathTargetId = pathSynchroDetail.Target.PathId;
            }
        }

        if ((tableReferenceSourceId !== null || pathSourceId !== null) &&
            (tableReferenceTargetId !== null || pathTargetId !== null)) {

            setTableReferenceSourceId(tableReferenceSourceId);
            setPathSourceId(pathSourceId);
            setTableReferenceTargetId(tableReferenceTargetId);

            let getPathSynchroCapabilitiesResult = await PathSynchroApi.GetPathSynchroCapabilitiesAsync(tableReferenceSourceId, pathSourceId, tableReferenceTargetId ?? -1, false);

            if (getPathSynchroCapabilitiesResult.IsFailed()) {
                alert("Error : " + getPathSynchroCapabilitiesResult.ErrorMessage);
                return;
            }

            let allFieldsSource = getPathSynchroCapabilitiesResult.Result.SourceFields
                .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);
            setSourceFields(allFieldsSource);

            let allFieldsTarget = getPathSynchroCapabilitiesResult.Result.TargetFields
                .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);
            setTargetFields(allFieldsTarget);

            setCapabilities(getPathSynchroCapabilitiesResult.Result.MappingsCapabilities);
            let transformationsCapabilitiesTemp = getPathSynchroCapabilitiesResult.Result.TransformationsCapabilities;
            transformationsCapabilitiesTemp.push(new TransformationNotConfigurableDto(0, "Direct", 0, new DataTypeDto(0, false, ""), new DataTypeDto(0, false, ""), "TransformationNotConfigurable"));
            setTransformationsCapabilities(transformationsCapabilitiesTemp);

            // dataLoad se relance à chaque nouvel objet Source/Target (ex. choix d'une itération dans l'onglet
            // Filters -> Source cloné). Tant que les tables ne changent pas, on repart du mapping EN COURS
            // d'édition (ref) et non du mapping sauvegardé : sinon les lignes non sauvegardées sont perdues
            // et le FilterBuilder n'a plus aucun champ coché à proposer.
            const loadedKey = `${tableReferenceSourceId}|${pathSourceId}|${tableReferenceTargetId}|${pathTargetId}`;
            const keepCurrentMappings = loadedKey === loadedKeyRef.current && pathSynchroMappingDtosRef.current.length > 0;
            loadedKeyRef.current = loadedKey;

            let Mappings = keepCurrentMappings ? pathSynchroMappingDtosRef.current : (pathSynchroDetail?.Mappings ?? []);

            let mappingTemp = buildMappingRows(Mappings, allFieldsTarget, allFieldsSource, getPathSynchroCapabilitiesResult.Result.MappingsCapabilities);

            setMapping(mappingTemp);

            let result = new Array<PathSynchroMappingDto>();

            mappingTemp.forEach((e: SynchroFieldMappingDto) => result.push(new PathSynchroMappingDto(e.IsKey, e.SourceFieldReferenceId ?? 0, e.TargetFieldReferenceId, e.TransformationType ?? 0, e.TransformationParameters ?? "", e.Filters, e.DeclaredVariables)));

            pathSynchroMappingDtosRef.current = result;
        } else {
            setCapabilities([]);
            setTransformationsCapabilities([]);
            setMapping([]);
            setTableReferenceSourceId(null);
            setPathSourceId(null);
            setTableReferenceTargetId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathSynchroDetail?.Source, pathSynchroDetail?.Target]);

    useEffect(() => {
        dataLoad()
    }, [dataLoad]);

    // The name is saved on its own (not part of the version): it never creates a version nor counts as an unsaved change.
    async function handleNameChange() {
        const newName = editNameInprogress;
        if (newName === null) {
            return;
        }

        const renameResult = await PathApi.RenamePathAsync(pathId, newName);
        if (renameResult.IsFailed()) {
            messageFormHandleRef.current?.Open(formatMessage("PathValidation_SaveFailed"), ErrorMessage);
            return;
        }

        setPathSynchroDetail((previous) => {
            if (previous === null) {
                return previous;
            }
            previous.Name = newName;
            return previous;
        });
        setEditNameInprogress(null);
        setToastConfig({
            ...toastConfig,
            isVisible: true,
            type: 'success',
            message: formatMessage('PathName_Saved'),
            withRedirect: false
        });
    }

    const handleDataChanges = useCallback((changes: DataChange[], origin: SynchroFieldMappingDto[]) => {
        dataChangesRef.current = changes;

        // Grid edits are unsaved changes too (the save buttons depend on it).
        if (changes.length > 0) {
            setChangeInprogress(true);
        }

        let result = new Array<PathSynchroMappingDto>();

        origin.forEach((e: SynchroFieldMappingDto) => result.push(new PathSynchroMappingDto(e.IsKey, e.SourceFieldReferenceId ?? 0, e.TargetFieldReferenceId, e.TransformationType ?? 0, e.TransformationParameters ?? "", e.Filters, e.DeclaredVariables)));

        changes.forEach((e: DataChange) => {

            let row = result.find((x) => x.TargetFieldReferenceId === e.key) ?? null;

            if (row !== null) {
                if (e.data.IsKey !== undefined) {
                    row.IsKey = e.data.IsKey;
                }
                if (e.data.SourceFieldReferenceId !== undefined) {
                    // Unlink pushes null; the DTO / backend use 0 as the "unmapped" sentinel and
                    // CheckBeforeSaveAndPublish skips rows on `=== 0`. Normalize like the origin mapping above.
                    row.SourceFieldReferenceId = e.data.SourceFieldReferenceId ?? 0;
                }
                if (e.data.TransformationType !== undefined) {
                    row.TransformationLogicId = e.data.TransformationType ?? 0;
                }
                if (e.data.TransformationParameters !== undefined) {
                    row.TransformationParameters = e.data.TransformationParameters;
                }
                if (e.data.Filters !== undefined) {
                    row.Filters = e.data.Filters;
                }
                if (e.data.DeclaredVariables !== undefined) {
                    row.DeclaredVariables = e.data.DeclaredVariables;
                }
            }
        });

        pathSynchroMappingDtosRef.current = result;

    }, []);

    const handleDataSourceRefreshed = useCallback(() => {
        dataLoad();
    }, [dataLoad]);

    const handleTableMappingSourceChange = useCallback((tableMappingReadDto: DataTableReadDto) => {
        setChangeInprogress(true);
        setPathSynchroDetail((previous) => {
            if (previous === null) {
                return previous;
            }

            // FiltersSelection réutilise ce setter pour pousser de simples changements de filtre/paramètres
            // (même table source, juste un nouvel objet Source) : le mapping ne doit être invalidé que si la
            // table source elle-même change, pas à chaque édition de filtre.
            const sameSourceTable = isSameDataTable(previous.Source, tableMappingReadDto);

            if (!sameSourceTable) {
                dataChangesRef.current = [];
            }

            return new GetPathSynchroDetailResult(
                previous.Id,
                previous.Name,
                tableMappingReadDto,
                previous.Target,
                sameSourceTable ? previous.Mappings : [],
                previous.Schedule,
                previous.Setup,
                previous.LastVersionId,
                previous.PublishedVersionId,
                previous.AdvancedFilterMode);
        });

    }, []);

    const handleTableMappingTargetChange = useCallback((tableMappingReadDto: DataTableReadDto) => {
        setChangeInprogress(true);
        setPathSynchroDetail((previous) => {
            if (previous === null) {
                return previous;
            }

            dataChangesRef.current = [];

            return new GetPathSynchroDetailResult(
                previous.Id,
                previous.Name,
                previous.Source,
                tableMappingReadDto,
                [],
                previous.Schedule,
                previous.Setup,
                previous.LastVersionId,
                previous.PublishedVersionId,
                previous.AdvancedFilterMode);
        });

    }, []);

    const handlePathSynchroSetupChange = useCallback((pathSynchroSetupDto: PathSynchroSetupDto) => {
        setChangeInprogress(true);
        setPathSynchroDetail((previous) => {
            if (previous === null) {
                return previous;
            }

            return new GetPathSynchroDetailResult(
                previous.Id,
                previous.Name,
                previous.Source,
                previous.Target,
                previous.Mappings,
                previous.Schedule,
                pathSynchroSetupDto,
                previous.LastVersionId,
                previous.PublishedVersionId,
                previous.AdvancedFilterMode);
        });

    }, []);

    const handlePathSynchroScheduleChange = useCallback((pathSynchroScheduleDto: ScheduleDto) => {
        setChangeInprogress(true);
        setPathSynchroDetail((previous) => {
            if (previous === null) {
                return previous;
            }

            return new GetPathSynchroDetailResult(
                previous.Id,
                previous.Name,
                previous.Source,
                previous.Target,
                previous.Mappings,
                pathSynchroScheduleDto,
                previous.Setup,
                previous.LastVersionId,
                previous.PublishedVersionId,
                previous.AdvancedFilterMode);
        });
    }, []);

    const handleAdvancedFilterModeChange = useCallback((value: boolean) => {
        if (advancedFilterModeLocked && !value) {
            return;
        }
        setAdvancedFilterMode(value);
        setChangeInprogress(true);
    }, [advancedFilterModeLocked]);

    function buildPathValidationInput(): PathBeforeSaveValidationInput {
        const selectedRows: ValidationRow[] = pathSynchroMappingDtosRef.current
            .filter((e) => e.SourceFieldReferenceId !== 0)
            .map((e) => ({
                labelFieldId: e.TargetFieldReferenceId,
                sourceFieldId: e.SourceFieldReferenceId,
                transformationLogicId: e.TransformationLogicId,
                transformationParameters: e.TransformationParameters,
                isKey: e.IsKey,
                targetReadOnly: targetFields.find((f) => f.Id === e.TargetFieldReferenceId)?.Type.IsReadOnly ?? false,
            }));

        return {
            hasData: pathSynchroDetail !== null,
            name: pathSynchroDetail?.Name,
            source: pathSynchroDetail?.Source,
            target: pathSynchroDetail?.Target,
            requireKey: true,
            selectedRows,
            transformationsCapabilities,
            getAllowedTransformationLogicIds: (row) =>
                GetPathSynchroMappingCapabilities(row.labelFieldId, row.sourceFieldId ?? 0)?.TransformationLogicIds ?? null,
            getFieldLabel: (fieldId) => GetFieldName(fieldId),
        };
    }

    function getFieldsResume(): SynchroFieldResume[] {
        return pathSynchroMappingDtosRef.current
            .filter((e) => (e.SourceFieldReferenceId ?? 0) !== 0 || (e.TransformationLogicId ?? 0) !== 0)
            .map((e) => ({
                SourceLabel: sourceFields.find((f) => f.Id === e.SourceFieldReferenceId)?.Name ?? "",
                TargetLabel: GetFieldName(e.TargetFieldReferenceId),
                IsKey: e.IsKey,
                TransformationLabel: (e.TransformationLogicId ?? 0) !== 0 ? (transformationsCapabilities.find((t) => t.LogicId === e.TransformationLogicId)?.Label ?? "") : "",
            }));
    }

    function CheckBeforeSaveAndPublish(): PathCheckResultDto {
        return checkPathBeforeSaveAndPublish(buildPathValidationInput());
    }

    async function save(publish: boolean): Promise<boolean> {
        const detail = pathSynchroDetail;
        if (detail === null || detail.Source === null || detail.Target === null || messageFormHandleRef.current === null) {
            messageFormHandleRef.current?.Open(formatMessage("PathValidation_TechnicalError"), ErrorMessage);
            return false;
        }
        const source = detail.Source;
        const target = detail.Target;

        return performPathSave({
            messageForm: messageFormHandleRef.current,
            checkResult: CheckBeforeSaveAndPublish(),
            checklist: getPathPublishChecklist(buildPathValidationInput()),
            publish,
            persist: () => PathSynchroApi.UpdatePathSynchroDetailAsync(pathId, detail.Name, pathSynchroMappingDtosRef.current, source, target, detail.Schedule, detail.Setup, advancedFilterMode, publish),
            onSuccess: (isPublished, savedVersionId) => {
                if (synchroMappingFieldsRef.current) {
                    // The Mapping grid is mounted (saving from the Mapping tab): let it flush its pending
                    // batch edits straight into the row objects held by `mapping` (the grid store mutates
                    // them in place), so that array stays the correct baseline for a later remount. We
                    // deliberately DON'T rebuild `mapping` in this case — a fresh array identity makes
                    // FieldGrid reload its data source and scroll the grid back to the top on every save.
                    synchroMappingFieldsRef.current.Save();
                } else {
                    // Saving from another tab — always the case for "Save and publish", which lives on the
                    // last tab. The Mapping grid is unmounted and can't self-flush, so rebuild `mapping`
                    // from the DTOs we just persisted. Without this the stale pre-edit baseline is shown
                    // when the user returns to the Mapping tab, until a full page reload refetches it.
                    setMapping(buildMappingRows(pathSynchroMappingDtosRef.current, targetFields, sourceFields, capabilities));
                }

                // Keep pathSynchroDetail.Mappings in sync with what was persisted so a later dataLoad()
                // (e.g. triggered by a data source refresh) rebuilds from the saved mapping, not the
                // original one. Copy the rows so the ref and the detail don't alias the same array.
                setPathSynchroDetail((previous) => previous === null ? previous : new GetPathSynchroDetailResult(
                    previous.Id,
                    previous.Name,
                    previous.Source,
                    previous.Target,
                    pathSynchroMappingDtosRef.current.map((m) => new PathSynchroMappingDto(m.IsKey, m.SourceFieldReferenceId, m.TargetFieldReferenceId, m.TransformationLogicId, m.TransformationParameters, m.Filters, m.DeclaredVariables)),
                    previous.Schedule,
                    previous.Setup,
                    // Keep the version ids in step with the save: the Synchronize panel offers the
                    // published and the last saved (draft) versions based on them.
                    savedVersionId ?? previous.LastVersionId,
                    isPublished ? (savedVersionId ?? previous.PublishedVersionId) : previous.PublishedVersionId,
                    previous.AdvancedFilterMode));

                if (advancedFilterMode) {
                    setAdvancedFilterModeLocked(true);
                }

                // The batch changes are now persisted; drop them so they aren't re-injected as pending edits
                // (bordeaux "modified" cells) when the Mapping grid remounts after a tab switch.
                dataChangesRef.current = [];

                setChangeInprogress(false);
                setIsPublished(isPublished);

                if (publish) {
                    setIsLaunchable(true);
                }

                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'success',
                    message: publish ? 'Path saved and published' : 'Path saved as draft',
                    withRedirect: false
                });
            },
        });
    }

    const onHiding = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false,
        });

        if (toastConfig.withRedirect) {
            navigate("/paths");
        }

    }, [toastConfig, navigate]);

    function cancel(): void {

        if (changeInprogress) {
            setIsOpen(true);
        }
        else {
            navigate("/paths");
        }
    }

    function cancelFromModal(): void {

        setIsOpen(false);
        navigate("/paths");
    }

    async function saveDraftFromModal(): Promise<void> {

        setIsOpen(false);
        if (await save(false)) {
            navigate("/paths");
        }
    }

    function changePage(index: number): void {
        // Onglet "Filters" indisponible (feature off ou mode avancé désactivé) : on l'enjambe dans le sens de navigation.
        const target = !filtersTabAvailable && index === FILTERS_TAB
            ? (index > currentTab ? index + 1 : index - 1)
            : index;
        setCurrentTab(target);
        setSearchParams({ tab: target.toString() }, { replace: true });
    };

    function back() {
        changePage(currentTab - 1);
    }

    function next() {
        changePage(currentTab + 1);
    }

    if (pathSynchroDetail === null) {
        return (<div>Waiting</div>);
    }

    let originTableId = (pathSynchroDetail.Source as DataSourceDataTableReadDto)?.TableReferenceId ?? null;
    let originPathId = (pathSynchroDetail.Source as PathDataTableReadDto)?.PathId ?? null;

    // Single "simple vs complex" filter state for the page: simple => the per-row FILTER column is
    // the editor and the Filters tab is hidden; complex => the reverse.
    const enterpriseMode = sessionDetail?.EnterpriseMode ?? false;

    return (
        <React.Fragment>
            <div style={{ width: '100%', height: '100%', padding: 24, flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', display: 'inline-flex' }}>
                <div style={{ alignSelf: 'stretch', height: '100%', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'flex' }}>
                    <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', minHeight: '49px' }}>
                        {editNameInprogress === null ?
                            <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 20, display: 'flex' }}>
                                <div style={{ color: '#E3E3E8', fontSize: 24, fontFamily: 'Manrope', fontWeight: '600', lineHeight: '36px', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                                    {pathSynchroDetail?.Name ?? "???"}
                                </div>
                                <img src="svgs/edit.svg" alt="circle" style={{ cursor: 'pointer' }} onClick={() => setEditNameInprogress(pathSynchroDetail?.Name ?? "???")} />
                            </div> :
                            <div style={{ width: '50%', justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'flex' }}>
                                <TextBox style={{ width: '50%' }} defaultValue={pathSynchroDetail?.Name ?? "???"} onValueChange={(e) => setEditNameInprogress(e)} />
                                <Button icon="check" onClick={() => handleNameChange()} />
                                <Button icon="close" onClick={() => setEditNameInprogress(null)} />
                            </div>}
                        <Button text="Close" type="normal" stylingMode="text" onClick={() => cancel()} />
                    </div>
                    <Tabs width='100%' stylingMode='secondary' onSelectedIndexChange={changePage} selectedIndex={currentTab} >
                        <Item text="Source">
                        </Item>
                        <Item text="Target">
                        </Item>
                        <Item text="Mapping">
                        </Item>
                        <Item text="Filters" visible={filtersTabAvailable} disabled={pathSynchroDetail.Source instanceof PathDataTableReadDto}>
                        </Item>
                        <Item text="Setup">
                        </Item>
                        <Item text="Summary">
                        </Item>
                        <Item text="Execution">
                        </Item>
                    </Tabs>
                    {(currentTab === 0) ? <SourceSelection OriginTableId={null} OriginPathId={null} Direction="Source" WithDataTable={true} TableMapping={pathSynchroDetail.Source ?? null} SetTableMapping={handleTableMappingSourceChange} DataSourceRefreshed={handleDataSourceRefreshed} /> : ""}
                    {(currentTab === 1) ? <SourceSelection OriginTableId={originTableId} OriginPathId={originPathId} Direction="Target" WithDataTable={false} TableMapping={pathSynchroDetail.Target ?? null} SetTableMapping={handleTableMappingTargetChange} DataSourceRefreshed={handleDataSourceRefreshed} /> : ""}
                    {(currentTab === 2) ? <SynchroMappingFields
                        ref={synchroMappingFieldsRef}
                        PathId={pathId}
                        MappingChanges={dataChangesRef.current}
                        SetMappingChanges={handleDataChanges}
                        SourceFields={sourceFields}
                        TargetFields={targetFields}
                        Capabilities={capabilities}
                        TransformationsCapabilities={transformationsCapabilities}
                        Mapping={mapping}
                        IsSimpleFilterMode={enterpriseMode}
                        FiltersEnabled={filterTabsEnabled && sourceHandlesFilters}
                        TableReferenceSourceId={tableReferenceSourceId}
                        PathSourceId={pathSourceId}
                        TableReferenceTargetId={tableReferenceTargetId ?? 0}
                        AdvancedFilterMode={advancedFilterMode}
                        SetAdvancedFilterMode={handleAdvancedFilterModeChange}
                        AdvancedFilterModeLocked={advancedFilterModeLocked} /> : ""}
                    {(currentTab === 3 && filtersTabAvailable) ? <FiltersSelection TableMapping={pathSynchroDetail.Source ?? null} SetTableMapping={handleTableMappingSourceChange} WithSimpleParameters={false} DeclaredParameters={collectDeclaredParameters(pathSynchroMappingDtosRef.current)} SelectedFieldReferenceIds={collectSelectedFieldReferenceIds(pathSynchroMappingDtosRef.current)} /> : ""}
                    {(currentTab === 4) ? <SynchroSetup TargetDataTable={pathSynchroDetail.Target} PathSynchroSetup={pathSynchroDetail.Setup} SetPathSynchroSetup={handlePathSynchroSetupChange} PathSchedule={pathSynchroDetail.Schedule} SetPathSchedule={handlePathSynchroScheduleChange} /> : ""}
                    {(currentTab === SUMMARY_TAB) ? <SynchroResume Source={pathSynchroDetail.Source ?? null} Target={pathSynchroDetail.Target ?? null} Fields={getFieldsResume()} Setup={pathSynchroDetail.Setup ?? null} Schedule={pathSynchroDetail.Schedule ?? null} /> : ""}
                    {(currentTab === MAX_TAB) ? <SynchroExecution PathId={pathId} IsLaunchable={isLaunchable} LastVersionId={pathSynchroDetail?.LastVersionId ?? null} PublishedVersionId={pathSynchroDetail?.PublishedVersionId ?? null} /> : ""}
                </div>
                <div style={{ width: '100%', height: 64, position: 'absolute', bottom: 32, left: 0, display: 'flex', justifyContent: 'space-between', padding: '16px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Button icon='back' text="Back to previous step" type="normal" stylingMode="text" onClick={() => back()} disabled={currentTab === 0} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        <PathVersionStatus HasUnsavedChanges={changeInprogress} IsPublished={isPublished} />
                        <Button text="Save as draft" type="normal" stylingMode="text" onClick={() => save(false)} disabled={!changeInprogress} />
                        {currentTab === MAX_TAB ?
                            <Button text="Save and publish" type="default" stylingMode="contained" onClick={() => save(true)} disabled={isPublished && !changeInprogress} /> :
                            <Button text="Continue to next step" type="default" stylingMode="contained" onClick={() => next()} />}
                    </div>
                </div>
            </div>
            <ChangeInProgressForm IsOpen={isOpen} SetIsOpen={setIsOpen} Ok={() => saveDraftFromModal()} Cancel={() => cancelFromModal()} />
            <MessageForm ref={messageFormHandleRef} />
            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                onHiding={onHiding}
                displayTime={1000}
            />
        </React.Fragment>
    )
}
